import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardHeader from "../../components/app_components/DashboardHeader";
import MobileTotalExpensesCard from "../../components/app_components/mobile/MobileTotalExpensesCard";
import SubscriptionList from "../../components/app_components/SubscriptionList";
import MobileSubscriptionList from "../../components/app_components/mobile/MobileSubscriptionList";
import MobileTrackedMerchantPromoList from "../../components/app_components/mobile/MobileTrackedMerchantPromoList";
import MobileDonutChart from "../../components/app_components/analytics/mobile/MobileDonutChart";
import PriceRadar from "../../components/app_components/analytics/PriceRadar";
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import MobileSubscriptionCollector from "../../components/app_components/mobile/MobileSubscriptionCollector";
import { Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, Share2, Loader2, Tag, Crown } from "lucide-react";
import FreeTrialList from "../../components/app_components/FreeTrialList";
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from "../../App";

const cn = (...classes) => classes.filter(Boolean).join(' ');

const MobileDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get httpClient from auth context
  
  const [showSubscriptionCollector, setShowSubscriptionCollector] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [freeTrials, setFreeTrials] = useState([]);
  const [isCollectorLoading, setIsCollectorLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [trackedMerchantsCount, setTrackedMerchantsCount] = useState(0);
  const [estimatedExpense, setEstimatedExpense] = useState({
    net_adjustment: 0,
    total_savings: 0,
    total_additional_cost: 0,
    is_net_saving: false,
    spaces_count: 0,
    summary: ''
  });
  const [isPremium, setIsPremium] = useState(false);
  const [isLoadingPremium, setIsLoadingPremium] = useState(true);
  const hasLoadedRef = useRef(false);

  // Create refs for functions that will be used in event listeners
  const checkUserSubscriptionsRef = useRef();
  const fetchFreeTrialsRef = useRef();
  const fetchTrackedMerchantsCountRef = useRef();
  const fetchEstimatedExpenseRef = useRef();
  const fetchPriceChangesRef = useRef();
  const fetchUnreadNotificationsCountRef = useRef();
  const fetchPremiumStatusRef = useRef();

  // Navigation items with translations
  const navItems = [
    { icon: Home, label: t('partners.nav.home'), path: "/mobile-dashboard" },
    { 
      icon: unreadNotificationsCount > 0 ? BellDot : Bell,
      label: t('partners.nav.notifications'),
      path: "/mobile-notifications",
      badge: unreadNotificationsCount > 0 ? unreadNotificationsCount : null
    },
    { icon: ShoppingCart, label: t('partners.nav.marketplace'), path: "/mobile-marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs'), path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('partners.nav.connectBank'), path: "/mobile-connectBank" },
    { icon: Users, label: t('partners.nav.partners'), path: "/mobile-partners" },
    { icon: Settings, label: t('partners.nav.settings'), path: "/mobile-account-settings" },
  ];

  // Check if initial setup is completed
  const checkInitialSetupStatus = async () => {
    try {
      if (!httpClient) return false;
      
      const response = await httpClient.request('/api/check-initial-setup');
      
      if (!response.ok) {
        console.log(`Setup check failed with status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && typeof data.completed === 'boolean') {
        return data.completed;
      }
      
      return false;
    } catch (err) {
      console.error('Error checking initial setup:', err);
      return false;
    }
  };

  // Fetch unread notifications count
  const fetchUnreadNotificationsCount = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadNotificationsCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Fetch price changes data
  const fetchPriceChanges = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/price-changes');
      
      if (response.ok) {
        const data = await response.json();
        setPriceData(data);
      } else {
        setPriceData([]);
      }
    } catch (err) {
      console.error('Error fetching price data:', err);
      setPriceData([]);
    }
  };

  // Fetch tracked merchants count
  const fetchTrackedMerchantsCount = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/user/tracked-merchants/count');
      
      if (response.ok) {
        const data = await response.json();
        setTrackedMerchantsCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching tracked merchants count:', error);
    }
  };

  // Fetch estimated expense adjustment from sharing spaces
  const fetchEstimatedExpense = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/sharing-space/user/estimated-expense');
      
      if (response.ok) {
        const data = await response.json();
        console.log("Mobile - Estimated expense data:", data);
        
        // Use the estimated_expense object
        setEstimatedExpense(data.estimated_expense || {
          net_adjustment: 0,
          total_savings: 0,
          total_additional_cost: 0,
          is_net_saving: false,
          spaces_count: 0,
          summary: ''
        });
      } else {
        console.error('Mobile - Failed to fetch estimated expense');
        setEstimatedExpense({
          net_adjustment: 0,
          total_savings: 0,
          total_additional_cost: 0,
          is_net_saving: false,
          spaces_count: 0,
          summary: ''
        });
      }
    } catch (error) {
      console.error('Mobile - Error fetching estimated expense:', error);
      setEstimatedExpense({
        net_adjustment: 0,
        total_savings: 0,
        total_additional_cost: 0,
        is_net_saving: false,
        spaces_count: 0,
        summary: ''
      });
    }
  };

  // Fetch premium status
  const fetchPremiumStatus = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/user/premium-status');
      
      if (response.ok) {
        const data = await response.json();
        setIsPremium(data.isPremium);
      } else {
        console.error('Failed to fetch premium status');
        setIsPremium(false);
      }
    } catch (error) {
      console.error('Error fetching premium status:', error);
      setIsPremium(false);
    } finally {
      setIsLoadingPremium(false);
    }
  };

  // Fetch tracked merchants for mobile list
  const fetchTrackedMerchants = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/user-tracked-merchants');
      
      if (response.ok) {
        const data = await response.json();
        // We don't need to store the full list, just update count
        setTrackedMerchantsCount(data.length || 0);
      }
    } catch (error) {
      console.error('Error fetching tracked merchants:', error);
    }
  };

  const fetchMerchants = async () => {
    setIsCollectorLoading(true);
    try {
      if (!httpClient) {
        setIsCollectorLoading(false);
        return;
      }
      
      const isSetupComplete = await checkInitialSetupStatus();
      
      if (isSetupComplete) {
        console.log("Initial setup already completed, hiding collector");
        setShowSubscriptionCollector(false);
        setIsCollectorLoading(false);
        return;
      }

      const response = await httpClient.request('/api/popular-merchant');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.merchants || !Array.isArray(data.merchants)) {
        setMerchants([]);
        setShowSubscriptionCollector(false);
        setIsCollectorLoading(false);
        return;
      }
      
      const validMerchants = data.merchants.filter(merchant => 
        merchant && merchant.id && merchant.name
      );
      
      if (validMerchants.length === 0) {
        setShowSubscriptionCollector(false);
        setIsCollectorLoading(false);
        return;
      }
      
      setMerchants(validMerchants);
      setShowSubscriptionCollector(true);
    } catch (err) {
      setMerchants([]);
      setShowSubscriptionCollector(false);
    } finally {
      setIsCollectorLoading(false);
    }
  };

  const checkUserSubscriptions = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/subscription-list');
      
      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return checkUserSubscriptions();
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setUserSubscriptions(data);
      }
    } catch (error) {
      console.error('Error checking subscriptions:', error);
      if (error.status === 401) {
        navigate("/demo");
      }
    }
  };

  const fetchFreeTrials = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/free-trial-list');
      
      if (response.ok) {
        const data = await response.json();
        setFreeTrials(data);
      }
    } catch (error) {
      console.error('Error fetching free trials:', error);
    }
  };

  // Refresh all dashboard data
  const refreshAllData = async () => {
    try {
      console.log('Mobile - Refreshing all dashboard data...');
      await Promise.all([
        checkUserSubscriptions(),
        fetchFreeTrials(),
        fetchTrackedMerchants(),
        fetchEstimatedExpense(),
        fetchPriceChanges(),
        fetchUnreadNotificationsCount(),
        fetchPremiumStatus()
      ]);
      console.log('Mobile - Dashboard data refreshed successfully');
    } catch (error) {
      console.error('Mobile - Error refreshing dashboard data:', error);
    }
  };

  // Update refs with the latest function instances on every render
  useEffect(() => {
    checkUserSubscriptionsRef.current = checkUserSubscriptions;
    fetchFreeTrialsRef.current = fetchFreeTrials;
    fetchTrackedMerchantsCountRef.current = fetchTrackedMerchants;
    fetchEstimatedExpenseRef.current = fetchEstimatedExpense;
    fetchPriceChangesRef.current = fetchPriceChanges;
    fetchUnreadNotificationsCountRef.current = fetchUnreadNotificationsCount;
    fetchPremiumStatusRef.current = fetchPremiumStatus;
  });

  // Event listener for subscription updates
  useEffect(() => {
    const handleSubscriptionsUpdated = () => {
      console.log('Mobile - Subscriptions updated event received, refreshing dashboard data...');
      
      // Use the refs to call the latest version of each function
      Promise.all([
        checkUserSubscriptionsRef.current?.(),
        fetchFreeTrialsRef.current?.(),
        fetchTrackedMerchantsCountRef.current?.(),
        fetchEstimatedExpenseRef.current?.(),
        fetchPriceChangesRef.current?.(),
        fetchUnreadNotificationsCountRef.current?.(),
        fetchPremiumStatusRef.current?.()
      ]).catch(error => {
        console.error('Mobile - Error in subscription update handler:', error);
      });
    };

    window.addEventListener('subscriptionsUpdated', handleSubscriptionsUpdated);
    
    return () => {
      window.removeEventListener('subscriptionsUpdated', handleSubscriptionsUpdated);
    };
  }, []);

  useEffect(() => {
    const initializeDashboard = async () => {
      // Check if user is authenticated
      if (!user) {
        navigate("/demo");
        return;
      }

      if (hasLoadedRef.current) {
        return;
      }

      try {
        setIsLoading(true);
        setIsCollectorLoading(true);
        hasLoadedRef.current = true;
        
        // Check setup status first, then fetch data accordingly
        const isSetupComplete = await checkInitialSetupStatus();
        
        if (!isSetupComplete) {
          await fetchMerchants();
        } else {
          setIsCollectorLoading(false);
        }
        
        // Fetch all other data in parallel
        await Promise.all([
          checkUserSubscriptions(),
          fetchPriceChanges(),
          fetchFreeTrials(),
          fetchUnreadNotificationsCount(),
          fetchTrackedMerchants(),
          fetchEstimatedExpense(),
          fetchPremiumStatus()
        ]);
        
      } catch (error) {
        console.error('Error initializing mobile dashboard:', error);
      } finally {
        setIsLoading(false);
        setInitialLoadComplete(true);
        
        // Small delay to ensure all state updates are processed
        setTimeout(() => {
          setShowContent(true);
        }, 50);
      }
    };

    initializeDashboard();
  }, [user, navigate, httpClient]);

  const handleSubscriptionComplete = async () => {
    try {
      if (!httpClient) return;
      
      await httpClient.request('/api/mark-initial-setup-completed', {
        method: "POST",
      });
      
      setShowSubscriptionCollector(false);
      
      // Refresh BOTH subscriptions and free trials
      await Promise.all([
        checkUserSubscriptions(),
        fetchFreeTrials(),
        fetchTrackedMerchants(),
        fetchEstimatedExpense()
      ]);
      
      // Dispatch event to notify other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('subscriptionsUpdated'));
      }
    } catch (error) {
      console.error('Error completing subscription:', error);
    }
  };

  const handleConnectToBank = () => {
    navigate("/mobile-connectBank");
  };

  // Calculate total expenses from personal subscriptions
  const totalPersonalExpenses = userSubscriptions.reduce((total, sub) => {
    const price = sub.price || 0;
    const monthlyPrice = sub.billing_interval === 'annual' ? price / 12 : price;
    return total + monthlyPrice;
  }, 0);

  // Adjusted total with sharing space estimated expense adjustment
  const adjustedTotalExpenses = totalPersonalExpenses - estimatedExpense.net_adjustment;

  const getSubscriptionData = () => {
    if (userSubscriptions.length === 0) {
      return [
        { name: "Streaming", value: 0 },
        { name: "Productivity", value: 0 },
        { name: "Cloud Storage", value: 0 },
        { name: "Professional", value: 0 }
      ];
    }
  
    const categories = userSubscriptions.reduce((acc, sub) => {
      const category = sub.category || 'Other';
      if (!acc[category]) {
        acc[category] = 0;
      }
      const monthlyValue = sub.billing_interval === 'annual' ? 
        (sub.price || 0) / 12 : 
        (sub.price || 0);
      acc[category] += monthlyValue;
      return acc;
    }, {});
  
    return Object.entries(categories).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2))
    }));
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Only show MobileSubscriptionCollector if we have valid merchants and initial load is complete */}
      {showSubscriptionCollector && !isCollectorLoading && merchants.length > 0 && initialLoadComplete && (
        <MobileSubscriptionCollector
          userId={user?.userId}
          merchants={merchants}
          onComplete={handleSubscriptionComplete}
          refreshDashboardData={refreshAllData}
        />
      )}

      <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
      
      <main className="flex-1 p-4 pb-20">
        {/* Welcome banner with conditional premium gradient */}
        <div className={`${isPremium 
          ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
          : 'bg-[#253985]'} text-white p-4 rounded-lg mb-4 transition-all duration-500`}
        >
          <h1 className="text-xl font-bold mb-1">
            {!initialLoadComplete || isLoading ? t('dashboard.loadingData', 'Loading your data...') : t('dashboard.welcomeTitle', 'Welcome to Your Dashboard')}
          </h1>
          <p className="text-white/90 text-sm">
            {!initialLoadComplete || isLoading ? t('dashboard.loadingSubscriptions', 'Please wait while we load your subscriptions') : t('dashboard.manageSubscriptions', 'Manage your subscriptions and view analytics')}
          </p>
        </div>

        {/* Show loading spinner only during initial load */}
        {(!initialLoadComplete || isLoading) ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
          </div>
        ) : (
          /* Fade in content when ready */
          <div className={`transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
            <div className="space-y-4 mb-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-md font-semibold mb-3">
                  {t('dashboard.monthlyExpenses', 'Monthly Expenses')}
                </h2>
                <MobileTotalExpensesCard 
                  total={adjustedTotalExpenses} 
                  originalTotal={totalPersonalExpenses}
                  activeSubscriptions={userSubscriptions.length} 
                  freeTrials={freeTrials}
                  estimatedExpense={estimatedExpense}
                />
              </div>

              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="h-64">
                  <MobileDonutChart
                    data={getSubscriptionData()}
                    category="name"
                    value="value"
                    colors={["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6"]}
                    valueFormatter={(value) => `€${value.toFixed(2)}`}
                    showLabel={true}
                    variant="donut"
                    className="h-full"
                    title={t('dashboard.spendingByCategory', 'Spending by Category')}
                  />
                </div>
              </div>

              {/* Price Radar Component for Mobile */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-md font-semibold">
                    {t('dashboard.priceRadar', 'Price Radar')}
                  </h2>
                  {/* Animated Radar Icon for mobile */}
                  <div className="relative">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <div className="absolute w-3 h-3 bg-blue-500 rounded-full opacity-75 animate-ping"></div>
                      <div className="absolute w-3 h-3 bg-blue-600 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-2 h-2">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
                          <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                          <path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                <PriceRadar priceData={priceData} userSubscriptions={userSubscriptions} />
              </div>
            </div>

            {/* Free Trials Section */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  {t('dashboard.activeFreeTrials', 'Active Free Trials')}
                </h2>
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">
                  {freeTrials.length} {t('dashboard.active', 'active')}
                </span>
              </div>
              <FreeTrialList 
                freeTrials={freeTrials} 
                refreshFreeTrials={fetchFreeTrials} 
              />
            </div>

            {/* Subscriptions Section */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-4">
              <h2 className="text-base font-semibold mb-3">
                {t('dashboard.yourSubscriptions', 'Your Subscriptions')}
              </h2>
              {userSubscriptions.length > 0 ? (
                <MobileSubscriptionList subscriptions={userSubscriptions} mobileView={true} />
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-3">
                    {t('dashboard.noSubscriptions', 'No subscriptions found')}
                  </p>
                  <button
                    onClick={() => setShowSubscriptionCollector(true)}
                    className="bg-[#253985] text-white py-2 px-4 rounded-lg text-sm font-medium"
                  >
                    {t('dashboard.addFirstSubscription', 'Add Your First Subscription')}
                  </button>
                </div>
              )}
            </div>

            {/* Tracked Merchants Section */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <Tag className="h-5 w-5 mr-2 text-blue-500" />
                  {t('dashboard.trackedMerchants', 'Tracked Merchants')}
                </h2>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {trackedMerchantsCount} {t('dashboard.tracked', 'tracked')}
                </span>
              </div>
              <MobileTrackedMerchantPromoList />
            </div>

            {/* Connect Bank Section */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-4">
              <div className="flex flex-col">
                <h2 className="text-base font-semibold mb-2">
                  {t('dashboard.connectBank', 'Connect Your Bank')}
                </h2>
                <p className="text-gray-600 text-sm mb-3">
                  {t('dashboard.autoTrackSubscriptions', 'Automatically track all your subscriptions by connecting your bank account')}
                </p>
                <button
                  onClick={handleConnectToBank}
                  className="bg-[#253985] text-white py-2 px-4 rounded-lg text-sm font-medium"
                >
                  {t('dashboard.connectBankAccount', 'Connect Bank Account')}
                </button>
              </div>
            </div>

            {/* Premium Status/Upgrade Section */}
            <div className="bg-gradient-to-r from-blue-50 to-amber-50 p-4 rounded-xl shadow-sm border border-gray-100 mb-4">
              {isLoadingPremium ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 text-[#253985] animate-spin mr-2" />
                  <span className="text-sm font-medium text-gray-900">
                    {t('premium.checkingStatus', 'Checking premium status...')}
                  </span>
                </div>
              ) : isPremium ? (
                <div className="flex flex-col items-center justify-center py-2">
                  <div className="flex items-center justify-center mb-2">
                    <Crown className="h-6 w-6 text-amber-500 mr-2" />
                    <span className="text-base font-semibold text-[#253985]">
                      {t('premium.title', 'EasySubs Premium')}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm text-center">
                    {t('premium.thankYou', 'Thank you for subscribing to EasySubs Premium!')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div className="flex items-center justify-center mb-3">
                    <Crown className="h-6 w-6 text-amber-500 mr-2" />
                    <span className="text-base font-medium text-gray-900">
                      {t('premium.features', 'EasySubs Premium Features')}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate("/mobile-upgrade")}
                    className="w-full py-2.5 bg-gradient-to-r from-[#253985] to-amber-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {t('premium.upgradeNow', 'Upgrade Now')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <MobileBottomNav navItems={navItems} currentPath={location.pathname} maxVisibleItems={5}/>
    </div>
  );
};

export default MobileDashboard;